import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { formatLocation } from '../../../utils/formatters';
import { 
  CalendarIcon, 
  UserGroupIcon, 
  MapPinIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import ErrorMessage from '../../../components/common/ErrorMessage';

const MyBookings = () => {
  const [activeTab, setActiveTab] = useState('upcoming');
  
  // Fetch bookings
  const fetchBookings = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const api = axios.create({
        baseURL: apiUrl
      });
      const response = await api.get('/api/v1/bookings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching bookings:', error);
      throw error;
    }
  };
  
  // Bookings query
  const { 
    data: bookings, 
    isLoading, 
    error,
    refetch 
  } = useQuery('bookings', fetchBookings, {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Filter bookings based on active tab
  const filteredBookings = bookings ? bookings.data.filter(booking => {
    const bookingDate = new Date(booking.booking_date);
    const travelDate = booking.travel_date ? new Date(booking.travel_date) : bookingDate;
    const today = new Date();
    
    if (activeTab === 'upcoming') {
      // A booking is upcoming if either the booking date or travel date is in the future
      return (bookingDate >= today || travelDate >= today) && booking.status !== 'cancelled';
    } else if (activeTab === 'past') {
      // A booking is past if both booking date and travel date are in the past or it's completed
      return (bookingDate < today && (!booking.travel_date || travelDate < today)) || booking.status === 'completed';
    } else if (activeTab === 'cancelled') {
      return booking.status === 'cancelled';
    }
    return true;
  }) : [];
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Get status badge based on booking status
  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Confirmed
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ExclamationCircleIcon className="h-4 w-4 mr-1" />
            Pending
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Completed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="h-4 w-4 mr-1" />
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };
  
  // Cancel booking
  const cancelBooking = async (bookingId) => {
    if (window.confirm('Are you sure you want to cancel this booking? Cancellation fees may apply.')) {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const api = axios.create({
          baseURL: apiUrl
        });
        await api.post(`/api/v1/bookings/${bookingId}/cancel`, {}, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        // Refetch bookings after cancellation
        refetch();
      } catch (error) {
        console.error('Error cancelling booking:', error);
        alert('Failed to cancel booking. Please try again later.');
      }
    }
  };
  
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <ErrorMessage 
          message="Failed to load your bookings. Please try again later." 
          onRetry={refetch} 
        />
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-800">My Bookings</h2>
        <p className="text-gray-600">Manage your tour bookings</p>
      </div>
      
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-6 py-4 text-sm font-medium ${
              activeTab === 'upcoming'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Upcoming
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`px-6 py-4 text-sm font-medium ${
              activeTab === 'past'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Past
          </button>
          <button
            onClick={() => setActiveTab('cancelled')}
            className={`px-6 py-4 text-sm font-medium ${
              activeTab === 'cancelled'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Cancelled
          </button>
        </nav>
      </div>
      
      {/* Bookings List */}
      <div className="p-6">
        {filteredBookings.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No bookings found</h3>
            <p className="text-gray-500 mb-4">
              {activeTab === 'upcoming'
                ? "You don't have any upcoming bookings."
                : activeTab === 'past'
                ? "You don't have any past bookings."
                : "You don't have any cancelled bookings."}
            </p>
            <Link
              to="/tours"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Browse Tours
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredBookings.map((booking) => (
              <div key={booking.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex flex-col md:flex-row">
                  {/* Tour Image */}
                  <div className="md:w-1/3 h-48 md:h-auto">
                    {booking.tour.featured_image ? (
                      <img
                        src={booking.tour.featured_image.startsWith('http') 
                          ? booking.tour.featured_image 
                          : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/storage/${booking.tour.featured_image}`}
                        alt={booking.tour.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.log('Image failed to load:', booking.tour.featured_image);
                          e.target.src = '/images/placeholder-tour.jpg';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400">No image available</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Booking Details */}
                  <div className="p-6 md:w-2/3">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-800 mb-1">
                          {booking.tour.name}
                        </h3>
                        <div className="flex items-center text-gray-500 text-sm mb-2">
                          <MapPinIcon className="h-4 w-4 mr-1" />
                          <span>{formatLocation(booking.tour.location)}</span>
                        </div>
                      </div>
                      <div className="mt-2 sm:mt-0">
                        {getStatusBadge(booking.status)}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-start">
                        <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <p className="text-xs text-gray-500">Tour Date</p>
                          <p className="font-medium">{formatDate(booking.booking_date)}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <p className="text-xs text-gray-500">Travelers</p>
                          <p className="font-medium">{booking.adults + (booking.children || 0)}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <p className="text-xs text-gray-500">Booking Date</p>
                          <p className="font-medium">{formatDate(booking.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <p className="text-xs text-gray-500">Total Price</p>
                          <p className="font-medium">${booking.total_amount}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <Link
                        to={`/dashboard/bookings/${booking.id}`}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                      >
                        View Details
                      </Link>
                      
                      {booking.status === 'confirmed' && new Date(booking.booking_date) > new Date() && (
                        <button
                          onClick={() => cancelBooking(booking.id)}
                          className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded text-red-700 bg-white hover:bg-red-50"
                        >
                          Cancel Booking
                        </button>
                      )}
                      
                      {booking.status !== 'cancelled' && (
                        <Link
                          to={`/tours/${booking.tour_id}`}
                          className="inline-flex items-center px-3 py-1.5 border border-primary text-sm font-medium rounded text-primary bg-white hover:bg-primary-50"
                        >
                          View Tour
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBookings;
